import {
  getCourse,
  getCommunityLinks,
  getLessons,
  type CommunityLink,
  type Course,
  type Lesson,
} from '@/app/(app)/learn/learn-data'
import {
  COMMUNITY_PAGE_COPY,
  LEARN_INDEX_COPY,
  LEARN_SIDEBAR_COPY,
  LESSON_PAGE_COPY,
  type CommunityPageCopy,
  type LearnIndexCopy,
  type LearnIndexGroupCopy,
  type LearnSidebarCopy,
  type LessonPageCopy,
} from '@/app/(app)/learn/learn-copy'
import { DOCS_LOCALES, type DocsLocale } from '@/lib/docs'
import { hasOverrides, pickOverride } from './contentOverrides'
import type { StaticRouteKeyEntry } from './routeStaticKeysTypes'

/**
 * Translation catalog for the `/learn` surface (the lesson index, individual
 * lesson pages, and the community page).
 *
 * The learn pages render hard-coded per-locale copy from `learn-data.ts`
 * (lesson/course/community content) and `learn-copy.ts` (page + sidebar chrome)
 * — none of it goes through next-intl `t()`, so next-intl never assigns these
 * strings an extract hash and the `/translate` editor can't see them. This
 * module exposes the same strings under stable dotted keys so the editor can:
 *
 *   - build a `value → key` inverse map (the in-iframe bridge tags rendered
 *     lesson/chrome text with `data-i18n-hash`, making it click-to-edit), and
 *   - list per-route static keys in the sidebar (so strings that aren't
 *     currently on screen are still editable).
 *
 * It mirrors `docsRouteMessages.ts`, which does the same for `/docs/*`.
 */

const LEARN_ROUTE = '/learn'
const LEARN_COMMUNITY_ROUTE = '/learn/community'

const LEARN_DATA_FILE = 'src/app/(app)/learn/learn-data.ts'
const LEARN_COPY_FILE = 'src/app/(app)/learn/learn-copy.ts'

function toLearnLocale(locale: string): DocsLocale {
  return (DOCS_LOCALES as readonly string[]).includes(locale) ? (locale as DocsLocale) : 'en'
}

// Stable slug for a group label so keys stay path-safe (`Using Martini` →
// `using-martini`). Labels are fixed English strings shared by the index page
// and the sidebar, so the same label always maps to the same key.
function groupSlug(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function courseKey(id: string, field: 'title' | 'description'): string {
  return `learn.course.${id}.${field}`
}

function lessonKey(slug: string, field: 'title' | 'description'): string {
  return `learn.lesson.${slug}.${field}`
}

function communityLinkKey(index: number, field: 'title' | 'description' | 'meta'): string {
  return `learn.communityLink.${index}.${field}`
}

/**
 * Flat `key → value` map of every learn string for one locale. Used to seed the
 * bridge inverse map and the sidebar's source/locale lookups.
 */
export function getLearnMessageCatalog(locale: string): Record<string, string> {
  const loc = toLearnLocale(locale)
  const out: Record<string, string> = {}

  const course = getCourse(loc)
  out[courseKey(course.id, 'title')] = course.title
  out[courseKey(course.id, 'description')] = course.description
  for (const lesson of course.lessons) {
    out[lessonKey(lesson.slug, 'title')] = lesson.title
    out[lessonKey(lesson.slug, 'description')] = lesson.description
  }

  getCommunityLinks(loc).forEach((link, index) => {
    out[communityLinkKey(index, 'title')] = link.title
    out[communityLinkKey(index, 'description')] = link.description
    if (link.meta) out[communityLinkKey(index, 'meta')] = link.meta
  })

  const index = LEARN_INDEX_COPY[loc]
  for (const [field, value] of Object.entries(index)) {
    if (field === 'groups') continue
    out[`learn.index.${field}`] = value as string
  }
  for (const [label, group] of Object.entries(index.groups)) {
    const slug = groupSlug(label)
    out[`learn.index.group.${slug}.label`] = group.label
    out[`learn.index.group.${slug}.description`] = group.description
  }

  const lessonPage = LESSON_PAGE_COPY[loc]
  for (const [field, value] of Object.entries(lessonPage)) {
    out[`learn.lessonPage.${field}`] = value
  }

  const communityPage = COMMUNITY_PAGE_COPY[loc]
  for (const [field, value] of Object.entries(communityPage)) {
    out[`learn.communityPage.${field}`] = value
  }

  const sidebar = LEARN_SIDEBAR_COPY[loc]
  for (const [field, value] of Object.entries(sidebar)) {
    if (field === 'groups') continue
    out[`learn.sidebar.${field}`] = value as string
  }
  for (const [label, value] of Object.entries(sidebar.groups)) {
    out[`learn.sidebar.group.${groupSlug(label)}`] = value
  }

  return out
}

function isLessonRoute(route: string): string | null {
  if (!route.startsWith(`${LEARN_ROUTE}/`)) return null
  const slug = route.slice(LEARN_ROUTE.length + 1)
  if (slug.includes('/')) return null
  const slugs = new Set(getLessons('en').map(lesson => lesson.slug))
  return slugs.has(slug) ? slug : null
}

/**
 * Static key entries for a learn route. Lets the sidebar list editable strings
 * — including ones not currently rendered — for `/learn`, `/learn/community`,
 * and each `/learn/<lesson-slug>` page. Chrome rendered by the shared docs shell
 * (the learn sidebar) is attached to every learn route.
 */
export function getLearnRouteStaticEntries(route: string, locale: string): StaticRouteKeyEntry[] {
  const lessonSlug = isLessonRoute(route)
  if (route !== LEARN_ROUTE && route !== LEARN_COMMUNITY_ROUTE && !lessonSlug) return []

  const loc = toLearnLocale(locale)
  const enCatalog = getLearnMessageCatalog('en')
  const localeCatalog = getLearnMessageCatalog(loc)
  const entries: StaticRouteKeyEntry[] = []

  function add(key: string, chain: string[], file: string) {
    const source = enCatalog[key]
    if (source === undefined) return
    entries.push({
      key,
      source,
      localeValue: localeCatalog[key],
      sources: [{ chain, file }],
    })
  }

  function addSidebarChrome() {
    for (const key of Object.keys(enCatalog)) {
      if (!key.startsWith('learn.sidebar.')) continue
      add(key, ['learn sidebar'], LEARN_COPY_FILE)
    }
  }

  if (route === LEARN_ROUTE) {
    // Index chrome (title/intro/quick links/suggest copy + group labels).
    for (const key of Object.keys(enCatalog)) {
      if (key.startsWith('learn.index.')) add(key, ['learn index'], LEARN_COPY_FILE)
    }
    // Lesson cards + the course heading.
    const course = getCourse('en')
    add(courseKey(course.id, 'title'), ['learn lessons', 'course title'], LEARN_DATA_FILE)
    add(courseKey(course.id, 'description'), ['learn lessons', 'course description'], LEARN_DATA_FILE)
    for (const lesson of course.lessons) {
      add(lessonKey(lesson.slug, 'title'), ['learn lessons', lesson.slug, 'title'], LEARN_DATA_FILE)
      add(lessonKey(lesson.slug, 'description'), ['learn lessons', lesson.slug, 'description'], LEARN_DATA_FILE)
    }
    // Community link cards.
    getCommunityLinks('en').forEach((_, index) => {
      add(communityLinkKey(index, 'title'), ['community', `link ${index + 1}`, 'title'], LEARN_DATA_FILE)
      add(communityLinkKey(index, 'description'), ['community', `link ${index + 1}`, 'description'], LEARN_DATA_FILE)
      add(communityLinkKey(index, 'meta'), ['community', `link ${index + 1}`, 'meta'], LEARN_DATA_FILE)
    })
    addSidebarChrome()
    return entries
  }

  if (route === LEARN_COMMUNITY_ROUTE) {
    for (const key of Object.keys(enCatalog)) {
      if (key.startsWith('learn.communityPage.')) add(key, ['learn community page'], LEARN_COPY_FILE)
    }
    getCommunityLinks('en').forEach((_, index) => {
      add(communityLinkKey(index, 'title'), ['community', `link ${index + 1}`, 'title'], LEARN_DATA_FILE)
      add(communityLinkKey(index, 'description'), ['community', `link ${index + 1}`, 'description'], LEARN_DATA_FILE)
      add(communityLinkKey(index, 'meta'), ['community', `link ${index + 1}`, 'meta'], LEARN_DATA_FILE)
    })
    addSidebarChrome()
    return entries
  }

  if (lessonSlug) {
    for (const key of Object.keys(enCatalog)) {
      if (key.startsWith('learn.lessonPage.')) add(key, ['learn lesson page'], LEARN_COPY_FILE)
    }
    add(lessonKey(lessonSlug, 'title'), ['learn lessons', lessonSlug, 'title'], LEARN_DATA_FILE)
    add(lessonKey(lessonSlug, 'description'), ['learn lessons', lessonSlug, 'description'], LEARN_DATA_FILE)
    addSidebarChrome()
    return entries
  }

  return entries
}

// ---------------------------------------------------------------------------
// Runtime override application
//
// The learn pages render from the hard-coded per-locale tables, not next-intl
// `t()`, so saved overrides don't reach them through request.ts. These helpers
// overlay overrides by the SAME keys the catalog above exposes. They're pure;
// the client pages fetch the override map via `usePublishedOverrides`, which is
// gated on `contentOverridesEnabled()` so production passes {} and renders the
// table verbatim.
// ---------------------------------------------------------------------------

// Overlay every string-valued field of a flat copy object (non-string fields,
// e.g. a nested `groups` map, are left untouched for the caller to handle).
function overlayStringFields<T extends object>(
  copy: T,
  overrides: Record<string, string>,
  keyFor: (field: string) => string,
): T {
  const out = { ...copy }
  const record = out as Record<string, unknown>
  for (const [field, value] of Object.entries(copy as Record<string, unknown>)) {
    if (typeof value !== 'string') continue
    record[field] = pickOverride(overrides, keyFor(field), value)
  }
  return out
}

export function applyLessonOverrides(lesson: Lesson, overrides: Record<string, string> | null | undefined): Lesson {
  if (!hasOverrides(overrides)) return lesson
  return {
    ...lesson,
    title: pickOverride(overrides, lessonKey(lesson.slug, 'title'), lesson.title),
    description: pickOverride(overrides, lessonKey(lesson.slug, 'description'), lesson.description),
  }
}

export function applyCourseOverrides(course: Course, overrides: Record<string, string> | null | undefined): Course {
  if (!hasOverrides(overrides)) return course
  return {
    ...course,
    title: pickOverride(overrides, courseKey(course.id, 'title'), course.title),
    description: pickOverride(overrides, courseKey(course.id, 'description'), course.description),
    lessons: course.lessons.map(lesson => applyLessonOverrides(lesson, overrides)),
  }
}

export function applyCommunityLinkOverrides(
  links: CommunityLink[],
  overrides: Record<string, string> | null | undefined,
): CommunityLink[] {
  if (!hasOverrides(overrides)) return links
  return links.map((link, index) => ({
    ...link,
    title: pickOverride(overrides, communityLinkKey(index, 'title'), link.title),
    description: pickOverride(overrides, communityLinkKey(index, 'description'), link.description),
    meta: link.meta !== undefined ? pickOverride(overrides, communityLinkKey(index, 'meta'), link.meta) : link.meta,
  }))
}

export function applyLearnIndexCopyOverrides(
  copy: LearnIndexCopy,
  overrides: Record<string, string> | null | undefined,
): LearnIndexCopy {
  if (!hasOverrides(overrides)) return copy
  const base = overlayStringFields(copy, overrides, field => `learn.index.${field}`)
  const groups: Record<string, LearnIndexGroupCopy> = {}
  for (const [label, group] of Object.entries(copy.groups)) {
    const slug = groupSlug(label)
    groups[label] = {
      label: pickOverride(overrides, `learn.index.group.${slug}.label`, group.label),
      description: pickOverride(overrides, `learn.index.group.${slug}.description`, group.description),
    }
  }
  return { ...base, groups }
}

export function applyLessonPageCopyOverrides(
  copy: LessonPageCopy,
  overrides: Record<string, string> | null | undefined,
): LessonPageCopy {
  if (!hasOverrides(overrides)) return copy
  return overlayStringFields(copy, overrides, field => `learn.lessonPage.${field}`)
}

export function applyCommunityPageCopyOverrides(
  copy: CommunityPageCopy,
  overrides: Record<string, string> | null | undefined,
): CommunityPageCopy {
  if (!hasOverrides(overrides)) return copy
  return overlayStringFields(copy, overrides, field => `learn.communityPage.${field}`)
}

export function applyLearnSidebarCopyOverrides(
  copy: LearnSidebarCopy,
  overrides: Record<string, string> | null | undefined,
): LearnSidebarCopy {
  if (!hasOverrides(overrides)) return copy
  const base = overlayStringFields(copy, overrides, field => `learn.sidebar.${field}`)
  const groups: Record<string, string> = {}
  for (const [label, value] of Object.entries(copy.groups)) {
    groups[label] = pickOverride(overrides, `learn.sidebar.group.${groupSlug(label)}`, value)
  }
  return { ...base, groups }
}
